import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import { useWindowSize } from '@allesauto/utils/hooks';
import { ArrowLeft16, ArrowRight16 } from '@carbon/icons-react';
import { Tab } from '@headlessui/react';
import clsx from 'clsx';
import React, {
  Children,
  FC,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useWhiteLabel } from '../whiteLabel';

export interface TabsProps {
  tabs: string[];
  defaultSelectedIndex?: number;
  selectedIndex?: number;
  className?: string;
  tabClassName?: string;
  contentWrapperClassName?: string;
  isDarkMode?: boolean;
  fullWidth?: boolean;
  onChange?: (index: number) => void;
  isPcpPage?: boolean;
}

const Tabs: FC<React.PropsWithChildren<TabsProps>> = (props) => {
  const {
    tabs,
    defaultSelectedIndex,
    selectedIndex,
    className: customClassName,
    tabClassName,
    contentWrapperClassName,
    isDarkMode = false,
    fullWidth = false,
    onChange,
    isPcpPage,
  } = props;

  const [hasScrollLeft, setHasScrollLeft] = useState(false);
  const [hasScrollRight, setHasScrollRight] = useState(false);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();
  const trackingIdentifier = `aa-tabs--button`;
  const whiteLabel = useWhiteLabel();

  const updateScrollState = useCallback(() => {
    if (listScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = listScrollRef.current;

      setHasScrollLeft(scrollLeft > 0);
      setHasScrollRight(
        scrollWidth > clientWidth && scrollWidth - scrollLeft > clientWidth
      );
    }
  }, []);

  useEffect(() => {
    updateScrollState();
  }, [windowSize, updateScrollState]);

  const scrollToLeft = useCallback(() => {
    if (listScrollRef.current) {
      const { offsetWidth, scrollLeft } = listScrollRef.current;

      listScrollRef.current.scroll({
        left: scrollLeft - offsetWidth,
        behavior: 'smooth',
      });
    }
  }, []);

  const scrollToRight = useCallback(() => {
    if (listScrollRef.current) {
      const { offsetWidth, scrollLeft } = listScrollRef.current;

      listScrollRef.current.scroll({
        left: scrollLeft + offsetWidth,
        behavior: 'smooth',
      });
    }
  }, []);

  const className = {
    tabListWrapperClass: `h-[50px] text-p1 relative overflow-hidden ${
      isPcpPage
        ? 'wl-tabList-wrapper-border'
        : 'wl-not-pcp-tabList-wrapper-border'
    }`,
    tabListScrollClass: 'overflow-x-auto h-full hide-scrollbar',
    tabListClass: 'h-full',
    tabListBtnClass: 'border-y-1px',
    inner: 'px-20px font-weight-bold AUREGO-button-color',
    tabBtnDefaultClass: `${
      isDarkMode
        ? 'text-white border-white ring-white'
        : 'text-purple80 border-purple80 ring-purple80 monochrome:!text-grey90 monochrome:!border-grey90 monochrome:!ring-grey90'
    } ring-inset hover:ring hover:border-none`,
    tabBtnDisabledClass: `${
      isDarkMode
        ? 'text-lightPurple monochrome:text-grey70 border-white'
        : 'text-lightPurple border-purple80 monochrome:text-grey70 monochrome:border-grey90'
    } cursor-default`,
    tabBtnSelectedClass: `${
      isDarkMode
        ? 'bg-green60 text-purple80 border-green60 monochrome:bg-grey10 monochrome:text-grey90 monochrome:border-grey10'
        : 'bg-purple80 text-white border-purple80 monochrome:bg-grey90 AUREGO-bg-color monochrome:!text-white monochrome:border-grey90'
    }`,
    tabScrollBtnWrapperClass: `absolute h-full w-[45px] top-0`,
    tabScrollBtnClass: `h-full w-full border-y-1px flex items-center justify-center ${
      isAuregoShop(whiteLabel) &&
      'monochrome:!text-grey90 monochrome:!border-grey90 monochrome:!ring-grey90'
    }`,
  };

  const hasScroll = hasScrollLeft || hasScrollRight;

  return (
    <Tab.Group
      defaultIndex={defaultSelectedIndex}
      selectedIndex={selectedIndex}
      manual
      onChange={onChange}
    >
      <div
        className={clsx(
          className.tabListWrapperClass,
          !fullWidth ? 'inline-block align-top' : null,
          customClassName
        )}
      >
        <div
          ref={listScrollRef}
          className={clsx(
            className.tabListScrollClass,
            hasScroll && 'mx-[45px]'
          )}
          onScroll={updateScrollState}
        >
          {hasScroll && (
            <div className={clsx(className.tabScrollBtnWrapperClass, 'left-0')}>
              <button
                className={clsx(
                  className.tabScrollBtnClass,
                  hasScrollLeft
                    ? className.tabBtnDefaultClass
                    : className.tabBtnDisabledClass,
                  'border-l-1px rounded-l-default',
                  hasScrollLeft && 'hover:pl-px'
                )}
                onClick={scrollToLeft}
              >
                <ArrowLeft16 aria-hidden="true" />
              </button>
            </div>
          )}

          <Tab.List
            className={clsx(
              className.tabListClass,
              fullWidth ? 'flex' : 'inline-flex'
            )}
          >
            {tabs.map((t, i) => (
              <Tab as={Fragment} key={t}>
                {({ selected }) => (
                  <button
                    data-tracking-id={`${trackingIdentifier}${i}`}
                    className={clsx(
                      className.tabListBtnClass,
                      {
                        'flex-1': fullWidth,
                      },
                      {
                        'rounded-l-default border-l-1px': !hasScroll && i === 0,
                        'hover:pl-px': !hasScroll && i === 0 && !selected,
                        'rounded-r-default border-r-1px':
                          !hasScroll && i === tabs.length - 1,
                        'hover:pr-px':
                          !hasScroll && i === tabs.length - 1 && !selected,
                      },
                      selected
                        ? className.tabBtnSelectedClass
                        : className.tabBtnDefaultClass,
                      tabClassName
                    )}
                  >
                    <div className={className.inner} id={`label--tab-${i}`}>
                      {t}
                    </div>
                  </button>
                )}
              </Tab>
            ))}
          </Tab.List>

          {hasScroll && (
            <div
              className={clsx(className.tabScrollBtnWrapperClass, 'right-0')}
            >
              <button
                className={clsx(
                  className.tabScrollBtnClass,
                  hasScrollRight
                    ? className.tabBtnDefaultClass
                    : className.tabBtnDisabledClass,
                  'border-r-1px rounded-r-default',
                  hasScrollRight && 'hover:pr-px'
                )}
                onClick={scrollToRight}
              >
                <ArrowRight16 aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
      {!!Children.count(props.children) && (
        <Tab.Panels className={contentWrapperClassName}>
          {Children.map(props.children, (ch, i) => (
            <Tab.Panel key={i}>{ch}</Tab.Panel>
          ))}
        </Tab.Panels>
      )}
    </Tab.Group>
  );
};

export default Tabs;
